import { useMemo, useState, useCallback } from 'react';

import { ScanDataWeekEndingOverviewFragment, ScanDataFileStatusType } from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';

import { getFileDisplayStatus, isReadyToSubmit } from '../scan-data-weekly-file.utils';
import { useScanDataSubmitWeeklyFile } from '../../../hooks/use-scan-data-submit-weekly-file.mutation.hook';

export type UseScanDataWeeklyFileSubmitParams = {
  data: ScanDataWeekEndingOverviewFragment | undefined;
  submissionDate?: string;
};

export type UseScanDataWeeklyFileSubmitReturn = {
  fileDisplayStatus: ReturnType<typeof getFileDisplayStatus>;
  canSubmit: boolean;
  isConfirmDialogOpen: boolean;
  setIsConfirmDialogOpen: (open: boolean) => void;
  handleSubmitClick: () => void;
  handleConfirmSubmit: () => Promise<void>;
  isSubmitting: boolean;
};

export const useScanDataWeeklyFileSubmit = ({
  data,
  submissionDate,
}: UseScanDataWeeklyFileSubmitParams): UseScanDataWeeklyFileSubmitReturn => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [submitWeeklyFile, { loading: isSubmitting }] = useScanDataSubmitWeeklyFile();

  const hasData = useMemo(() => {
    const weekEndPerformance = data?.weekEndPerformance;
    return (weekEndPerformance?.reportedLines || 0) > 0;
  }, [data?.weekEndPerformance]);

  const fileDisplayStatus = useMemo(() => {
    const backendStatus = data?.fileStatus || ScanDataFileStatusType.NotSubmitted;
    return getFileDisplayStatus(backendStatus, hasData);
  }, [data?.fileStatus, hasData]);

  const canSubmit = isReadyToSubmit(fileDisplayStatus);

  const handleSubmitClick = useCallback(() => {
    setIsConfirmDialogOpen(true);
  }, []);

  const onCompleted = useCallback(() => {
    snackbar.success('Weekly file submitted successfully');
    setIsConfirmDialogOpen(false);
  }, []);

  const onError = useCallback(() => {
    snackbar.error('Failed to submit weekly file. Please try again.');
    setIsConfirmDialogOpen(false);
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    if (!submissionDate) {
      return;
    }

    await submitWeeklyFile({
      variables: {
        input: {
          submissionDate,
        },
      },
      onCompleted,
      onError,
    });
  }, [submitWeeklyFile, submissionDate, onCompleted, onError]);

  return {
    fileDisplayStatus,
    canSubmit,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    handleSubmitClick,
    handleConfirmSubmit,
    isSubmitting,
  };
};
